import { RowViewport } from "@tento-lona/sheets/viewport";
import { RowKey, type Option, type RowLocalId } from "@tento-lona/sheets";
import type { SheetRow, SheetViewModel } from "@tento-lona";
import type { SheetBinder } from "../sheet-index-renderer.ts";

/**
 * The one owner of a sheet element's `RowResidency`: the element's row
 * viewport and its default binder read the same instance, built once per
 * (sheet, layout, rows) instead of once by each (F11 step 1).
 */
export class RowResidencyOwner<
  S extends RowResidencyOwner.Geometry = SheetViewModel.Surface<SheetBinder>,
  R = RowResidency,
> {
  #current: Option<{
    readonly sheet: S;
    readonly layout: S["layout"];
    readonly rows: S["joinedTree"]["flattened"]["rows"];
    readonly value: R;
  }> = null;

  constructor(readonly build: (sheet: S) => R) {}

  /** The production owner: builds a `RowResidency` of the sheet. */
  static create(): RowResidencyOwner {
    return new RowResidencyOwner((sheet) => new RowResidency(sheet));
  }

  /** The residency of `sheet` as it is now (rebuilt when its sheet, layout or rows changed). */
  of(sheet: S): R {
    const rows = sheet.joinedTree.flattened.rows;
    const current = this.#current;
    if (current != null && current.sheet === sheet && current.layout === sheet.layout && current.rows === rows) {
      return current.value;
    }
    const value = this.build(sheet);
    this.#current = { sheet, layout: sheet.layout, rows, value };
    return value;
  }

  /** Rebuild on the next read (a binder's mode reset). */
  invalidate(): void {
    this.#current = null;
  }
}

/** Types that go with `RowResidencyOwner`: `Geometry`. */
export namespace RowResidencyOwner {
  /** What the owner keys a residency by: the sheet, its layout and its flattened rows. */
  export type Geometry = {
    readonly layout: object;
    readonly joinedTree: { readonly flattened: { readonly rows: readonly object[] } };
  };
}

/** Complete semantic geometry, independent from resident DOM slots. */
export class RowResidency {
  readonly index: RowViewport.Index;
  readonly #owners: RowViewport.Index;
  readonly semanticIndices = new Map<RowLocalId, number>();
  readonly rowsBySerial = new Map<string, SheetRow.Joined.Handle>();
  constructor(readonly sheet: SheetViewModel.Surface<SheetBinder>) {
    const rows = sheet.joinedTree.flattened.rows;
    const geometry = rows.map((row, semanticIndex): RowViewport.Row => {
      const layout = sheet.layout.getRowLayout(row.id);
      if (layout == null) throw new Error(`row residency: missing geometry for ${row.id}`);
      const section = sheet.joinedTree.getRowLocation(row.id) ?? "body";
      this.semanticIndices.set(row.id, semanticIndex);
      this.rowsBySerial.set(row.id.serial, row);
      return { rowId: row.id, semanticIndex, section,
        topPx: layout.offsetPx - (section === "body" ? sheet.layout.headerHeightPx : 0),
        heightPx: layout.cellHeightPx };
    });
    this.index = new RowViewport.Index(geometry, sheet.layout.bodyHeightPx);
    // A span/container owner can paint across children even when its own row
    // rectangle is above the window. Hold only intersecting owner rectangles.
    const owners = geometry.flatMap((row) => {
      const layout = sheet.layout.getRowLayout(row.rowId);
      return layout != null && layout.cellHeightWithChildrenPx > row.heightPx
        ? [{ ...row, heightPx: layout.cellHeightWithChildrenPx }] : [];
    });
    this.#owners = new RowViewport.Index(owners, sheet.layout.bodyHeightPx);
  }
  /** Current wire aliases resolve through the authoritative identity scope. */
  rowForWire(wire: string): Option<SheetRow.Joined.Handle> {
    const id = this.sheet.handle.model.registry.get(RowKey.of(wire));
    if (id == null) return null;
    const row = this.rowsBySerial.get(id.serial);
    return row?.id === id ? row : null;
  }

  select(input: RowViewport.Input): { selection: RowViewport.Selection; rows: SheetRow.Joined.Handle[] } {
    const selection = this.index.select(input.mode === "all" ? input : {
      ...input, pinned: [...input.pinned, ...this.#owners.select(input).rows.map(({ row }) => row.rowId)],
    });
    const complete = this.sheet.joinedTree.flattened.rows;
    return { selection, rows: selection.rows.map(({ row }) => {
      const selected = complete[row.semanticIndex];
      if (selected == null || selected.id !== row.rowId) throw new Error("row residency: projection changed");
      return selected;
    }) };
  }
}
